import { Card } from '@/components/ui/card';
import { useMemo, useState } from 'react';

import { useMyTenants } from '@/api/tenant';

import { CustomerIdentitySearch } from '../customerIdentity/customer-identity-search';
import { CustomerIdentityList } from '../customerIdentity/customer-identity-list';
import { CustomerIdentityLivePreview } from '../customerIdentity/customer-identity-live-preview';
import type { CustomerIdentityPreview } from '../customerIdentity/types/types';

export const CustomerIdentitySection = () => {
  const { data: tenants } = useMyTenants();
  const [selectedPreview, setSelectedPreview] = useState<CustomerIdentityPreview | null>(null);
  const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(false);
  const [search, setSearch] = useState('');

  const filteredTenants = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();

    if (!normalizedSearch) {
      return tenants;
    }

    return tenants.flatMap((tenant) => {
      const tenantMatches = tenant.name.toLocaleLowerCase().includes(normalizedSearch);

      if (tenantMatches) {
        return [
          {
            ...tenant,
            subTenants: [],
          },
        ];
      }

      const matchingSubTenants = tenant.subTenants.filter((subTenant) =>
        subTenant.name.toLocaleLowerCase().includes(normalizedSearch)
      );

      if (!matchingSubTenants.length) {
        return [];
      }

      return [
        {
          ...tenant,
          subTenants: matchingSubTenants,
        },
      ];
    });
  }, [search, tenants]);

  return (
    <section className="grid gap-6 pb-12 xl:grid-cols-[minmax(0,1fr)_320px]">
      <Card className="order-2 flex min-w-0 flex-col gap-4 p-4 xl:order-1">
        <CustomerIdentitySearch value={search} onChange={setSearch} />
        <CustomerIdentityList
          tenants={filteredTenants}
          isSearchActive={Boolean(search.trim())}
          selectedTenantId={selectedPreview?.id}
          onPreviewChange={setSelectedPreview}
        />
      </Card>
      <Card className="order-1 flex min-w-0 flex-col gap-4 p-4 xl:order-2">
        <CustomerIdentityLivePreview
          preview={selectedPreview}
          isCollapsed={isPreviewCollapsed}
          onToggle={() => setIsPreviewCollapsed((current) => !current)}
        />
      </Card>
    </section>
  );
};
